'use client';

// cSpell:ignore subtitl

import type { CompleteTrackingParams } from '@/app/(home-page)/types';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo, type CSSProperties } from 'react';
import { useInView } from 'react-intersection-observer';
import { buildHomeButtonTrackingParams } from '../../tracking';
import type {
  ImageTextGridNavDisplayStyle,
  ImageTextGridNavItem,
  ImageTextGridNavJumpConfig,
  ImageTextGridNavProps,
} from './types';

const COMPONENT_ID = 'imageTextGridNav';
const MIN_NAV_ITEMS = 3;
const MAX_NAV_ITEMS = 9;

interface StyleOneImagePresentation {
  imageSize: number;
  left: number;
  mixBlendMode?: CSSProperties['mixBlendMode'];
  objectFit: CSSProperties['objectFit'];
  rotate: number;
  size: number;
  top: number;
}

// 装修数据不提供素材偏移参数，所有导航项统一使用 Figma 节点 1618:16458 的展示规则。
const STYLE_ONE_IMAGE_PRESENTATION: StyleOneImagePresentation = {
  imageSize: 144.54,
  left: -11.5,
  objectFit: 'contain',
  rotate: 15,
  size: 177.025,
  top: -33.23,
  mixBlendMode: 'multiply',
};

const formatText = (text?: string) => text?.replace(/<br\s*\/?>/gi, '\n') || '';

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const getItemLabel = (item: ImageTextGridNavItem, index: number) =>
  item.title || item.sku?.short_name || item.product_group?.name || `Navigation item ${index + 1}`;

const buildTrackingParams = ({
  item,
  itemIndex,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  item: ImageTextGridNavJumpConfig;
  itemIndex: number;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): CompleteTrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  mkt_index: itemIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
  module_title: moduleTitle,
});

function ImageTextGridNavItemView({
  item,
  itemIndex,
  displayStyle,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: ImageTextGridNavItem;
  itemIndex: number;
  displayStyle: ImageTextGridNavDisplayStyle;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
} & Pick<ImageTextGridNavProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const trackingParams = useMemo(
    () =>
      buildTrackingParams({
        item,
        itemIndex,
        moduleIndex,
        componentId,
        moduleId,
        moduleTitle,
      }),
    [componentId, item, itemIndex, moduleId, moduleIndex, moduleTitle],
  );
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });
  const itemRef = useMergedRefs(exposureRef, validExposureRef);

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('ImageTextGridNav exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(trackingParams);
    } catch (error) {
      logger.error('ImageTextGridNav click tracking error:', error);
    }
  }, [onTrack, trackingParams]);

  const label = getItemLabel(item, itemIndex);
  const imageAlt = /\blens(?:es)?\b/i.test(moduleTitle)
    ? `${label} Lenses`
    : /\bshapes?\b/i.test(moduleTitle)
      ? `${label} Glasses`
      : label;
  const imageWidth = getImageSize(item.jump_img_width, displayStyle === 1 ? 92 : 32);
  const imageHeight = getImageSize(item.jump_img_height, displayStyle === 1 ? 92 : 32);
  const styleOneImagePresentation = STYLE_ONE_IMAGE_PRESENTATION;

  if (displayStyle === 2) {
    return (
      <div ref={itemRef} className="min-w-0">
        <MagicJump
          prefetch
          item={item}
          onBeforeJump={handleClick}
          aria-label={label}
          className="flex h-[44px] w-full min-w-0 items-center gap-[4px] rounded-[28px] px-[12px] py-[4px]"
          style={{ backgroundColor: item.text_bg_color || '#ffffff' }}
        >
          <span className="relative block size-[32px] shrink-0 overflow-hidden">
            <Image
              src={item.jump_image}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              loading={moduleIndex < 3 ? 'eager' : 'lazy'}
              className="block size-full object-contain"
            />
          </span>
          <span
            className="min-w-0 flex-1 truncate font-br text-[12px] font-normal leading-[14px]"
            style={{ color: item.text_color || '#282828' }}
          >
            {formatText(label)}
          </span>
        </MagicJump>
      </div>
    );
  }

  return (
    <div ref={itemRef} className="min-w-0 self-start">
      <MagicJump
        prefetch
        item={item}
        onBeforeJump={handleClick}
        aria-label={label}
        className="mx-auto flex w-full max-w-[109px] min-w-0 flex-col items-center gap-[6px]"
      >
        <span className="relative block size-[92px] shrink-0 overflow-hidden rounded-full bg-[#f7f7f7]">
          <span
            className="pointer-events-none absolute flex items-center justify-center"
            style={{
              left: styleOneImagePresentation.left,
              top: styleOneImagePresentation.top,
              width: styleOneImagePresentation.size,
              height: styleOneImagePresentation.size,
              mixBlendMode: styleOneImagePresentation.mixBlendMode,
            }}
          >
            <span
              className="relative block shrink-0"
              style={{
                width: styleOneImagePresentation.imageSize,
                height: styleOneImagePresentation.imageSize,
                aspectRatio: '1 / 1',
                transform: `rotate(${styleOneImagePresentation.rotate}deg)`,
              }}
            >
              <Image
                fill
                src={item.jump_image}
                alt={imageAlt}
                sizes={`${Math.ceil(styleOneImagePresentation.imageSize)}px`}
                loading={moduleIndex < 3 ? 'eager' : 'lazy'}
                className="pointer-events-none absolute inset-0 max-w-none"
                style={{ objectFit: styleOneImagePresentation.objectFit }}
              />
            </span>
          </span>
        </span>
        <span
          className="w-full whitespace-pre-line break-words text-center font-br text-[12px] font-normal leading-[14px]"
          style={{
            color: item.text_color || '#1a1c1e',
            backgroundColor: item.text_bg_color || '#ffffff',
          }}
        >
          {formatText(label)}
        </span>
      </MagicJump>
    </div>
  );
}

export default function ImageTextGridNav({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  onBtnTrack,
}: ImageTextGridNavProps) {
  const { deviceType } = useContext(ResponsiveContext);
  // 2026 新组件只读取新版 content 契约，不兼容旧装修组件的扁平数据。
  const content = indexData.content;
  const navItems = useMemo(
    () =>
      (content?.jump_target || [])
        .filter((item) => Boolean(item.jump_image && item.title))
        .slice(0, MAX_NAV_ITEMS),
    [content?.jump_target],
  );
  const displayStyle = Number(content?.display_style);

  if (
    deviceType === EPlatform.WEB ||
    !content ||
    (displayStyle !== 1 && displayStyle !== 2) ||
    navItems.length < MIN_NAV_ITEMS
  ) {
    return null;
  }

  const normalizedDisplayStyle = displayStyle as ImageTextGridNavDisplayStyle;
  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const moduleTitle = content.layout_title || '';
  const shouldShowHeader =
    Number(content.layout_title_status) === 1 &&
    Boolean(content.layout_title || content.layout_subtitle);
  const shouldShowViewMore =
    Number(content.view_more_status) === 1 &&
    Boolean(content.btn_text && content.jump_type && content.Jump_target);
  const viewMoreButtonName = content.btn_text || '';
  const handleViewMoreClick = () => {
    if (!onBtnTrack) return;

    try {
      onBtnTrack(
        buildHomeButtonTrackingParams({
          indexData,
          moduleIndex,
          moduleTitle,
          buttonName: viewMoreButtonName,
        }),
      );
    } catch (error) {
      logger.error('ImageTextGridNav view more tracking error:', error);
    }
  };

  return (
    <section
      data-component-id={componentId}
      className={normalizedDisplayStyle === 2 ? 'w-full pb-[20px]' : 'w-full pb-[12px]'}
      style={
        Number(content.bg_color_status) === 1 && content.bg_color
          ? { backgroundColor: content.bg_color }
          : undefined
      }
    >
      {shouldShowHeader && (
        <header className="px-[12px] pt-[20px] pb-[16px] text-left">
          {content.layout_title && (
            <h3
              className="line-clamp-2 whitespace-pre-line font-bb text-[20px] leading-[24px]"
              style={{ color: content.layout_title_color || '#282828' }}
            >
              {formatText(content.layout_title)}
            </h3>
          )}
          {content.layout_subtitle && (
            <h4
              className="mt-[4px] line-clamp-3 whitespace-pre-line font-br text-[12px] leading-[14px]"
              style={{
                color: content.layout_subtitle_color || content.layout_subtitl_color || '#666666',
              }}
            >
              {formatText(content.layout_subtitle)}
            </h4>
          )}
        </header>
      )}

      <div
        className={
          normalizedDisplayStyle === 1
            ? 'grid grid-cols-3 gap-x-[12px] gap-y-[12px] px-[6px]'
            : 'grid grid-cols-2 gap-[8px] px-[12px]'
        }
      >
        {navItems.map((item, itemIndex) => (
          <ImageTextGridNavItemView
            key={item.id || `image-text-grid-nav-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
            displayStyle={normalizedDisplayStyle}
            moduleIndex={moduleIndex}
            componentId={componentId}
            moduleId={moduleId}
            moduleTitle={moduleTitle}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </div>

      {shouldShowViewMore && (
        <div className="flex justify-center px-[12px] pt-[12px] pb-[12px]">
          <MagicJump
            prefetch
            item={content}
            onBeforeJump={handleViewMoreClick}
            className="flex h-[40px] w-full items-center justify-center rounded-[20px] border px-[20px] font-bb text-[14px] leading-[17px]"
            style={{
              color: content.btn_word_color || '#282828',
              backgroundColor: content.btn_bg_color || '#ffffff',
              borderColor: content.btn_word_color || '#282828',
            }}
          >
            {content.btn_text}
          </MagicJump>
        </div>
      )}
    </section>
  );
}
